import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-4538">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4539">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-4540">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-4541">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-4542" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-4543">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-4544" />
              <p className="text-sm text-gray-600" data-id="element-4545">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-4546">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-4547" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-4548">
          {/* Quality & Metadata */}
          <div data-id="element-4549">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4550">
              <Brain className="w-3.5 h-3.5" data-id="element-4551" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-4552">
              <div className="grid grid-cols-2 gap-4" data-id="element-4553">
                <div data-id="element-4554">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4555">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-4556">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-4557">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4558">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-4559">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-4560">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-4561">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-4562">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4563">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4564">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-4565">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4566">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4567">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-4568">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4569">
              <Database className="w-3.5 h-3.5" data-id="element-4570" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-4571">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4572" />
                <span className="text-sm text-gray-600" data-id="element-4573">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-4574">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-4575">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-4576">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4577">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-4578" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-4579">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-4580">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4581" />
                  <span className="text-sm" data-id="element-4582">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-4583">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4584" />
                  <span className="text-sm" data-id="element-4585">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-4586">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4587">
              <BookOpen className="w-3.5 h-3.5" data-id="element-4588" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-4589">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4590">
                      <div data-id="element-4591">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4592">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4593">
                          {row.pipelineIssues.some(i => i.code === 'SEED_DOK_MISSING') ? <span className="text-amber-600" data-id="element-4594">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-4595">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4596">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4597">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-4598">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_PARTIAL') ? <span className="text-amber-500" data-id="element-4599">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-4600">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-4601">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4602" />
                        <span className="text-sm text-gray-600" data-id="element-4603">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-4604">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-4605" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-4606" />}
                        <span className="text-sm text-gray-600" data-id="element-4607">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}